/**
 * React hook for accessing current admin's permissions
 * Story 5.10: Role-Based Access Control
 */

import { useQuery } from '@tanstack/react-query';
import {
  AdminRole,
  Permission,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  getPermissionsForRole,
  ROLE_PERMISSIONS,
} from '@/lib/permissions';

interface AdminMeResponse {
  data: {
    id: string;
    email: string;
    role: AdminRole;
    totpEnabled: boolean;
  };
}

interface UsePermissionsResult {
  /** Current admin's role */
  role: AdminRole | null;
  /** Current admin's ID */
  adminId: string | null;
  /** Current admin's email */
  email: string | null;
  /** List of permissions the current admin has */
  permissions: Permission[];
  /** Check if admin has a specific permission */
  can: (permission: Permission) => boolean;
  /** Check if admin has any of the specified permissions */
  canAny: (permissions: Permission[]) => boolean;
  /** Check if admin has all of the specified permissions */
  canAll: (permissions: Permission[]) => boolean;
  /** Whether permissions are still loading */
  isLoading: boolean;
  /** Whether there was an error loading permissions */
  isError: boolean;
  /** Error object if loading failed */
  error: Error | null;
}

/**
 * Hook to access current admin's permissions
 *
 * @example
 * ```tsx
 * const { can, isLoading } = usePermissions();
 *
 * if (isLoading) return <Spinner />;
 *
 * return (
 *   <>
 *     {can('team.view') && <Link href="/team">Team</Link>}
 *     {can('costs.view') && <Link href="/costs">Costs</Link>}
 *   </>
 * );
 * ```
 */
export function usePermissions(): UsePermissionsResult {
  const { data, isLoading, isError, error } = useQuery<AdminMeResponse>({
    queryKey: ['admin-me'],
    queryFn: async () => {
      const response = await fetch('/api/admin/auth/me');
      if (!response.ok) {
        throw new Error('Failed to fetch admin permissions');
      }
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });

  const role = data?.data?.role ?? null;
  const adminId = data?.data?.id ?? null;
  const email = data?.data?.email ?? null;
  const permissions = role ? getPermissionsForRole(role) : [];

  return {
    role,
    adminId,
    email,
    permissions,
    can: (permission: Permission) => hasPermission(role, permission),
    canAny: (perms: Permission[]) => hasAnyPermission(role, perms),
    canAll: (perms: Permission[]) => hasAllPermissions(role, perms),
    isLoading,
    isError,
    error: error as Error | null,
  };
}

/**
 * Hook to check a single permission with loading state
 * Useful for conditionally rendering components
 *
 * @example
 * ```tsx
 * const { allowed, isLoading } = useHasPermission('team.manage');
 * if (!allowed) return null;
 * return <ManageTeamButton />;
 * ```
 */
export function useHasPermission(permission: Permission): {
  allowed: boolean;
  isLoading: boolean;
} {
  const { can, isLoading } = usePermissions();
  return {
    allowed: can(permission),
    isLoading,
  };
}
